// Pure helpers for revoking manual distributions (kept out of JSX so they can be unit tested).
import {
  BB_REVOKE_MAX_IDS,
  BB_REVOKE_REASON_MAX,
  BbManualDistribution,
  BbRevokeDistributionsResult,
} from '../../../services/blindBoxService';

/** Decimal string / number → finite number (0 for blanks and garbage). */
export const decimalToNum = (v: string | number | null | undefined): number => {
  const n = typeof v === 'number' ? v : Number(String(v ?? '').trim());
  return Number.isFinite(n) ? n : 0;
};

export const isRevoked = (r: Pick<BbManualDistribution, 'revokedAt'>): boolean => !!r.revokedAt;

export const shortfallOf = (r: Pick<BbManualDistribution, 'clawbackShortfall'>): number =>
  decimalToNum(r.clawbackShortfall);

/**
 * What the revoke action means for a record:
 *  - 'revoke': not revoked yet
 *  - 'retry':  revoked, but some USDC could not be clawed back yet (shortfall > 0)
 *  - null:     revoked and fully settled — nothing left to do
 */
export function revokeActionFor(
  r: Pick<BbManualDistribution, 'revokedAt' | 'clawbackShortfall'>
): 'revoke' | 'retry' | null {
  if (!isRevoked(r)) return 'revoke';
  return shortfallOf(r) > 0 ? 'retry' : null;
}

/** Validates the revoke dialog input; returns an i18n key or null. */
export function validateRevoke(ids: string[], reason: string): string | null {
  if (ids.length < 1) return 'module.blindBox.mdRevokeErrNoSelection';
  if (ids.length > BB_REVOKE_MAX_IDS) return 'module.blindBox.mdRevokeErrTooMany';
  const r = reason.trim();
  if (!r) return 'module.blindBox.mdRevokeErrReasonRequired';
  if (r.length > BB_REVOKE_REASON_MAX) return 'module.blindBox.mdRevokeErrReasonTooLong';
  return null;
}

/** Totals for the result summary: USDC clawed back by this call and the shortfall still owed. */
export function summarizeRevoke(res: BbRevokeDistributionsResult): {
  clawedBackNow: number;
  remainingShortfall: number;
  boxesRevoked: number;
} {
  return (res.results || []).reduce(
    (acc, row) => ({
      clawedBackNow: acc.clawedBackNow + decimalToNum(row.clawedBackNow),
      remainingShortfall: acc.remainingShortfall + (row.record ? shortfallOf(row.record) : 0),
      boxesRevoked: acc.boxesRevoked + (row.boxesRevoked || 0),
    }),
    { clawedBackNow: 0, remainingShortfall: 0, boxesRevoked: 0 }
  );
}
